/**
 * ModalShell — 공유 모달 껍데기 (오버레이 / z-index / ESC / 외부클릭 / 헤더·푸터)
 * ----------------------------------------------------------------------------
 * 보고서 §5.4 / §8.3 흡수:
 *  - z-index 9000~10200 난립 → --z-modal / --z-modal-high
 *  - 백드롭 색 8종 → --overlay (+ blur 옵션)
 *  - ESC 닫기(기본 on) / 외부클릭 닫기(기본 off)
 *  - 너비 프리셋 sm/md/lg/xl/full
 *
 * 흡수하는 것: 오버레이·중앙정렬·z-index·닫기 처리·헤더/푸터 골격.
 * 흡수하지 않는 것: 내부 콘텐츠, 폼 스키마, 데이터 흐름 (호출부가 children 으로 통과).
 *
 * ★ 모양 = DESIGN-AI §11 (기준: Partners 파트너사 추가) — 가운데 · 1px gray-200 테두리 · 그림자 없음 ·
 *   머리/발 구분선 · 제목 15px bold · X 18px gray-400 · 발 = [footerStart(삭제)] …… [footer(취소 → 주요)].
 *   ★ 오버레이에 ds-scope 를 붙이지 않는다 — 붙이면 호출부가 넘긴 본문(스타일 없는 input·button)의
 *   브라우저 기본 테두리까지 지워진다. 대신 이 껍데기의 선은 border-solid 를 직접 적는다(preflight:false).
 *
 * 사용 예:
 *   <ModalShell open={open} onClose={onClose} size="sm"
 *     title="제목" icon={<Clock size={18}/>}
 *     footer={<Button onClick={onClose}>확인</Button>}>
 *     <p>본문</p>
 *   </ModalShell>
 *
 * ★ 모션: 데스크톱은 그대로 modal-pop(150ms). 폰(모바일 셸, html.in-mobile-shell)에서는
 *   - sm = 하단 시트(위치는 src/mobile/mobileShell.css) — 아래에서 올라오고(translateY 100%→0 · 280ms),
 *     위에 손잡이 막대가 붙는다. 손잡이·머리를 아래로 끌어 80px 넘게 내리거나 빠르게 튕기면 onClose.
 *   - 그 외 = 전체화면 — 아래에서 올라온다(끌어 닫기는 없음: 본문 스크롤과 겹친다).
 *   prefers-reduced-motion 이면 모션 없이 바로 뜬다.
 */
import React from "react";
import { X } from "lucide-react";
import { useModalDismiss } from "./useModalDismiss";
import { useLanguage } from "./LanguageProvider";
import { EASE } from "../../lib/motion";

/** 모션 줄이기 — matchMedia 가 없는 환경(jsdom 테스트)에서도 터지지 않게 */
const prefersReduced = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const inMobileShell = () => typeof document !== "undefined" && document.documentElement.classList.contains("in-mobile-shell");

const SHEET_IN_MS = 280;
/** 끌어 닫기 기준 — 80px 넘게 내리거나, 0.5px/ms 보다 빠르게 아래로 튕기면 닫는다 */
const DRAG_CLOSE_PX = 80;
const FLICK_PX_PER_MS = 0.5;

export type ModalSize = "sm" | "md" | "lg" | "xl" | "full";

type Props = {
  open: boolean;
  onClose: () => void;
  size?: ModalSize;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  /** 헤더 우측, X 앞에 들어가는 액션 슬롯 */
  headerActions?: React.ReactNode;
  footer?: React.ReactNode;
  /** 발 왼쪽 끝 슬롯 — 삭제처럼 파괴적인 동작 (수정 모드만) */
  footerStart?: React.ReactNode;
  children: React.ReactNode;
  dismissOnBackdrop?: boolean;
  dismissOnEsc?: boolean;
  /** 백드롭 blur 효과 */
  blur?: boolean;
  /** 더 높은 스택이 필요할 때 (--z-modal-high) */
  elevated?: boolean;
  /** 패널 추가 스타일 */
  panelStyle?: React.CSSProperties;
  /** X 닫기 버튼 숨김 */
  hideClose?: boolean;
  "aria-label"?: string;
};

/* 모든 프리셋은 좁은 뷰포트에서 calc(100vw - 32px)로 캡. sm=확인창 · md=기본(Partners 720) · lg=넓게 */
const WIDTH: Record<ModalSize, string> = {
  sm: "min(420px, calc(100vw - 32px))",
  md: "min(720px, calc(100vw - 32px))",
  lg: "min(1100px, calc(100vw - 32px))",
  xl: "min(1240px, calc(100vw - 32px))",
  full: "calc(100vw - 32px)",
};

export default function ModalShell({
  open,
  onClose,
  size = "md",
  title,
  subtitle,
  icon,
  headerActions,
  footer,
  footerStart,
  children,
  dismissOnBackdrop = false,
  dismissOnEsc = true,
  blur = false,
  elevated = false,
  panelStyle,
  hideClose = false,
  "aria-label": ariaLabel,
}: Props) {
  const { t } = useLanguage();
  const { backdropProps, panelRef, zIndex } = useModalDismiss({
    open,
    onClose,
    dismissOnEsc,
    dismissOnBackdrop,
  });

  // 폰 셸 여부는 열릴 때 한 번 본다(셸 클래스는 화면 폭이 바뀔 때만 붙었다 떨어진다)
  const mobile = open && inMobileShell();
  const sheet = mobile && size === "sm";
  const backdropRef = React.useRef<HTMLDivElement>(null);

  // 폰: 아래에서 올라오기 + 백드롭 페이드 — 첫 프레임 전에 시작
  React.useLayoutEffect(() => {
    if (!open || !inMobileShell() || prefersReduced()) return;
    panelRef.current?.animate?.([{ transform: "translateY(100%)" }, { transform: "translateY(0)" }], { duration: SHEET_IN_MS, easing: EASE.out });
    backdropRef.current?.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: SHEET_IN_MS, easing: EASE.out });
  }, [open, panelRef]);

  // 하단 시트 끌어 닫기 — 손잡이·머리에서 포인터(터치 포함)로
  const drag = React.useRef<{ id: number; y0: number; t0: number; lastY: number; lastT: number; v: number } | null>(null);
  const onDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!sheet) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // 머리 안 버튼(X·액션)·입력은 끌기 대상이 아니다
    if ((e.target as HTMLElement).closest("button, a, input, select, textarea, [role='button']")) return;
    drag.current = { id: e.pointerId, y0: e.clientY, t0: e.timeStamp, lastY: e.clientY, lastT: e.timeStamp, v: 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const panel = panelRef.current;
    if (panel) panel.style.transition = "none";
  };
  const onDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dt = e.timeStamp - d.lastT;
    if (dt > 0) d.v = (e.clientY - d.lastY) / dt;
    d.lastY = e.clientY;
    d.lastT = e.timeStamp;
    const dy = Math.max(0, e.clientY - d.y0); // 위로는 끌리지 않는다
    const panel = panelRef.current;
    if (panel) panel.style.transform = dy ? `translateY(${dy}px)` : "";
  };
  const onDragEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    const dy = Math.max(0, e.clientY - d.y0);
    const panel = panelRef.current;
    if (!panel) return;
    const shouldClose = e.type !== "pointercancel" && (dy > DRAG_CLOSE_PX || (dy > 10 && d.v > FLICK_PX_PER_MS));
    const ms = prefersReduced() ? 0 : 200;
    if (shouldClose) {
      // 끝까지 내려 보낸 뒤 닫는다
      panel.style.transition = ms ? `transform ${ms}ms ${EASE.in}` : "none";
      panel.style.transform = "translateY(100%)";
      const close = () => {
        onClose();
        // 호출부가 닫지 않으면(확인 필요 등) 시트를 제자리로 돌린다 — 닫혔으면 떨어진 노드라 무해
        window.setTimeout(() => { panel.style.transition = "none"; panel.style.transform = ""; }, 0);
      };
      if (ms) window.setTimeout(close, ms); else close();
    } else {
      // 제자리로 되돌아간다
      panel.style.transition = ms ? `transform ${ms}ms ${EASE.out}` : "none";
      panel.style.transform = "";
    }
  };
  const dragHandlers = sheet
    ? { onPointerDown: onDragStart, onPointerMove: onDragMove, onPointerUp: onDragEnd, onPointerCancel: onDragEnd }
    : undefined;

  if (!open) return null;

  const hasHeader = title != null || icon != null || headerActions != null || !hideClose;

  return (
    <div
      {...backdropProps}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      data-modal-backdrop=""
      className={`fixed inset-0 flex items-center justify-center p-6${mobile ? "" : " bg-black/40"}`}
      style={{
        backdropFilter: blur ? "blur(4px)" : undefined,
        // 열린 창 스택 순서로 쌓는다(useModalDismiss). elevated 는 같은 층에서 한 칸 더 위.
        zIndex: zIndex + (elevated ? 5 : 0),
      }}
    >
      {/* 폰에서는 백드롭을 따로 깔아 패널과 별개로 페이드한다(데스크톱은 바깥 div 의 bg-black/40 그대로) */}
      {mobile && <div ref={backdropRef} aria-hidden="true" className="pointer-events-none absolute inset-0 bg-black/40" />}
      <div
        ref={panelRef}
        // 폰(모바일 셸)에서는 크기에 따라 전체화면/하단 시트로 바뀐다 — src/mobile/mobileShell.css
        data-modal-size={size}
        className="relative flex max-h-[90vh] max-w-full flex-col overflow-hidden rounded-xl border border-solid border-gray-200 bg-white text-gray-800"
        style={{ width: WIDTH[size], animation: mobile || prefersReduced() ? "none" : "modal-pop 0.15s ease", ...panelStyle }}
      >
        {sheet && (
          // 손잡이 막대 — 끌어 내려 닫는 시트라는 신호. 누르는 면은 막대보다 넓게(높이 20px · 폭 전체)
          <div {...dragHandlers} aria-hidden="true" className="flex h-5 flex-shrink-0 cursor-grab items-center justify-center" style={{ touchAction: "none" }}>
            <span className="block h-1 w-9 rounded-pill bg-gray-300" />
          </div>
        )}
        {hasHeader && (
          <div {...dragHandlers} className={`flex flex-shrink-0 items-center gap-3 border-0 border-b border-solid border-gray-200 px-5 ${sheet ? "pb-3.5 pt-1" : "py-3.5"}`} style={sheet ? { touchAction: "none" } : undefined}>
            {icon && <div className="flex flex-shrink-0 items-center text-gray-500">{icon}</div>}
            <div className="min-w-0 flex-1">
              {title != null && <strong className="block text-[15px] font-bold leading-snug text-gray-800">{title}</strong>}
              {subtitle != null && <div className="mt-0.5 text-[12px] text-gray-500">{subtitle}</div>}
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              {headerActions}
              {!hideClose && (
                <button type="button" onClick={onClose} aria-label={t("닫기", "Close")} className="-mr-1.5 flex h-8 w-8 items-center justify-center rounded border-0 bg-transparent p-0 text-gray-400 outline-none hover:bg-gray-100 hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-blue-600/30">
                  {/* 누르는 목표 32px(피츠 R1) — 아이콘은 18px 그대로 */}
                  <X size={18} />
                </button>
              )}
            </div>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto">{children}</div>

        {(footer != null || footerStart != null) && (
          <div className="flex flex-shrink-0 items-center justify-between gap-2 border-0 border-t border-solid border-gray-200 px-5 py-3">
            <div className="flex items-center gap-2">{footerStart}</div>
            <div className="flex items-center gap-2">{footer}</div>
          </div>
        )}
      </div>
    </div>
  );
}
